import React, { useState, useEffect, useCallback, useRef } from "react";
import GoogleMapReact from "google-map-react";
import { Input } from "antd";
import { EnvironmentOutlined, CheckCircleFilled } from "@ant-design/icons";
import { useTranslation } from "react-i18next";

// Default view: Saudi Arabia (Riyadh), zoomed out enough to show the whole country.
const DEFAULT_CENTER = { lat: 24.7136, lng: 46.6753 };
const DEFAULT_ZOOM = 5;

// google-map-react anchors a child's top-left corner on the coordinate, so shift the pin up
// and left so that its tip sits exactly on the point.
const Marker = ({ selected, color }) => (
  <svg
    width="40"
    height="40"
    viewBox="0 0 24 24"
    style={{
      transform: "translate(-50%, -100%)",
      filter: "drop-shadow(0 2px 3px rgba(0,0,0,0.35))",
      opacity: selected ? 1 : 0.55,
      pointerEvents: "none",
    }}
  >
    <path
      d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"
      fill={selected ? color : "#8c8c8c"}
      stroke="#fff"
      strokeWidth="1.2"
    />
    <circle cx="12" cy="9" r="2.8" fill="#fff" />
  </svg>
);

const LocationPicker = ({
  // Hides the green "Location selected" line below the map (the pin and the filled address already show it).
  compact = false,
  apiKey,
  locationLink,
  onLocationSelect,
  // Reports the text currently in the search box, so a parent form can require it to be filled.
  onSearchTextChange,
  initialAddress,
  // Saved coordinates of an existing record, so editing opens on the stored spot.
  initialPosition,
  defaultCenter = DEFAULT_CENTER,
  defaultZoom = DEFAULT_ZOOM,
  markerColor = "#ef4444",
}) => {
  const { t } = useTranslation();
  const hasInitialPosition =
    typeof initialPosition?.lat === "number" && typeof initialPosition?.lng === "number";
  const [searchInput, setSearchInput] = useState(initialAddress || "");
  const [selectedPlace, setSelectedPlace] = useState(null);
  const [hasSelection, setHasSelection] = useState(hasInitialPosition);
  const [mapCenter, setMapCenter] = useState(hasInitialPosition ? initialPosition : defaultCenter);
  const [zoom, setZoom] = useState(hasInitialPosition ? 15 : defaultZoom);
  const mapCenterRef = useRef(mapCenter);
  mapCenterRef.current = mapCenter;

  // A saved address usually arrives after the first render (the record is fetched async), so
  // fill the search box once it shows up - but never over something already typed or picked.
  useEffect(() => {
    if (initialAddress && !searchInput) setSearchInput(initialAddress);
  }, [initialAddress]);

  useEffect(() => {
    onSearchTextChange?.(searchInput);
  }, [searchInput]);

  useEffect(() => {
    if (locationLink) {
      const match = locationLink.match(/q=(-?\d+\.\d+),(-?\d+\.\d+)/);
      if (match) {
        const lat = parseFloat(match[1]);
        const lng = parseFloat(match[2]);
        setMapCenter({ lat, lng });
        setZoom(17);
        setHasSelection(true);
        fetchAddressFromCoordinates(lat, lng);
      }
    }
  }, [locationLink]);

  // The saved position usually arrives after the first render (the event is fetched async).
  // Only move the map if it differs from where the pin already is, so picking a place
  // (which writes the same coordinates back into the form) doesn't reset the zoom.
  useEffect(() => {
    if (!hasInitialPosition) return;
    const { lat, lng } = mapCenterRef.current;
    if (lat === initialPosition.lat && lng === initialPosition.lng) return;
    setMapCenter({ lat: initialPosition.lat, lng: initialPosition.lng });
    setZoom(15);
    setHasSelection(true);
  }, [initialPosition?.lat, initialPosition?.lng]);

  const fetchAddressFromCoordinates = (lat, lng) => {
    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ location: { lat, lng } }, (results, status) => {
      if (status === "OK" && results[0]) {
        handlePlaceSelect(results[0], null, window.google.maps);
      }
    });
  };

  const handleApiLoaded = ({ map, maps }) => {
    const autocomplete = new maps.places.Autocomplete(
      document.getElementById("location-search"),
      { types: ["establishment", "geocode"] }
    );

    autocomplete.addListener("place_changed", () => {
      const place = autocomplete.getPlace();
      handlePlaceSelect(place, map, maps);
    });
  };

  const handlePlaceSelect = (place, map, maps) => {
    if (!place.geometry) return;

    const { location } = place.geometry;
    const newCenter = {
      lat: location.lat(),
      lng: location.lng(),
    };

    setMapCenter(newCenter);
    setZoom(17);
    setSelectedPlace(place);
    setHasSelection(true);

    const addressData = {
      name: place.name || "",
      address: place.formatted_address || "",
      types: place.types || [],
      location: newCenter,
      phoneNumber: place.international_phone_number || "",
      googleLocationLink: `https://www.google.com/maps?q=${newCenter.lat},${newCenter.lng}`,
    };

    place.address_components?.forEach((component) => {
      const types = component.types;
      if (types.includes("postal_code")) {
        addressData.postalCode = component.long_name;
      } else if (types.includes("country")) {
        addressData.country = component.long_name;
      } else if (types.includes("administrative_area_level_1")) {
        addressData.state = component.long_name;
      } else if (types.includes("locality")) {
        addressData.city = component.long_name;
      }
    });

    onLocationSelect(addressData);
    setSearchInput(place.formatted_address || "");
  };

  const handleMapClick = useCallback(({ lat, lng, x, y, event }) => {
    setMapCenter({ lat, lng });
    setHasSelection(true);
    fetchAddressFromCoordinates(lat, lng);
  }, []);

  return (
    <div className="space-y-2">
      <div className="text-xs text-gray-600">{t("locationPicker.instruction")}</div>
      <div className="relative">
        <Input
          id="location-search"
          placeholder={t("locationPicker.searchPlaceholder")}
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          prefix={<EnvironmentOutlined />}
          style={{ width: "100%" }}
        />
      </div>

      <div style={{ height: "230px", width: "100%", position: "relative" }}>
        <GoogleMapReact
          bootstrapURLKeys={{ key: apiKey, libraries: ["places"] }}
          center={mapCenter}
          zoom={zoom}
          onClick={handleMapClick}
          yesIWantToUseGoogleMapApiInternals
          onGoogleApiLoaded={handleApiLoaded}
        >
          <Marker lat={mapCenter.lat} lng={mapCenter.lng} selected={hasSelection} color={markerColor} />
        </GoogleMapReact>
        {!hasSelection && (
          <div
            style={{
              position: "absolute",
              top: 8,
              left: "50%",
              transform: "translateX(-50%)",
              background: "rgba(0,0,0,0.7)",
              color: "#fff",
              fontSize: 12,
              padding: "4px 10px",
              borderRadius: 999,
              pointerEvents: "none",
              whiteSpace: "nowrap",
            }}
          >
            {t("locationPicker.tapToPlace")}
          </div>
        )}
      </div>

      {!compact && hasSelection && (searchInput || selectedPlace) && (
        <div className="text-xs text-green-600 flex items-start gap-1">
          <CheckCircleFilled style={{ marginTop: 2 }} />
          <span>
            {t("locationPicker.selected")}
            {searchInput ? `: ${searchInput}` : ""}
          </span>
        </div>
      )}
    </div>
  );
};

export default LocationPicker;
